/**
 * The conversation log's codes in plain words: why an email went to a person, and how sure the
 * assistant was of the reply it chose.
 */
import type { ThreadSelection } from './api/api';

const SELECTION: Record<string, string> = {
  several_requests: 'the email asked for several things at once',
  no_category: 'no reply fits this kind of email',
  unsure_category: 'it wasn’t clear which kind of email this is',
  no_template: 'no reply fits this email',
  unsure_template: 'two replies fitted about equally well',
  unavailable: 'choosing a reply failed',
  unknown_template: 'the chosen reply is no longer live',
};

const LICENSING = new Set([
  'producer_scope_verified',
  'training_current',
  'firm_insurance_allowed',
  'appointment_rule_satisfied',
  'state_product_enabled',
  'insurance_content_use_verified',
]);

export function reasonInWords(reason: string | null): string {
  if (!reason) return 'a person needed to answer';
  const [kind, detail = ''] = reason.split(/:(.*)/s);
  switch (kind) {
    case 'library_empty':
      return 'no replies are live yet';
    case 'selection':
      return SELECTION[detail] ?? 'no reply fitted closely enough';
    case 'client':
      return detail === 'ambiguous'
        ? 'the name matched more than one client'
        : 'it wasn’t clear which client the email is about';
    case 'ineligible':
      if (LICENSING.has(detail)) return 'it needs a licensing check Waterlily doesn’t have yet';
      if (detail === 'client_access_current') return 'it’s about a client the advisor can’t see';
      return 'the advisor couldn’t be verified for this reply';
    case 'render':
      if (detail.startsWith('field_missing') || detail.startsWith('condition_undecided'))
        return 'information the reply needs couldn’t be read';
      if (detail.startsWith('step_unavailable'))
        return 'one of the reply’s next steps can’t be offered for this email';
      return 'the reply failed its final check';
    default:
      return reason;
  }
}

/** "Chosen with high confidence · took 1.4 seconds". */
export function choiceInWords(selection: ThreadSelection): string | null {
  const confidence = selection.selection?.template?.confidence;
  const parts = [
    typeof confidence === 'number'
      ? `Chosen with ${confidence >= 0.9 ? 'high' : confidence >= 0.75 ? 'good' : 'low'} confidence`
      : null,
    selection.durationMs !== null
      ? `took ${(selection.durationMs / 1000).toFixed(1)} seconds`
      : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}
